/**
 * Folgefragen-Editor (Punkt 129, Mockup entwurf-6.html Karte „Folgefragen“): je Feld „Immer zeigen“
 * oder „Nur zeigen, wenn …“ (Feld X hat diese Antwort). Ein Feld erscheint erst, wenn eine Antwort
 * davor es braucht; so bleibt das Formular kurz.
 *
 * Vor dem Speichern gemeldet, nicht erst als 422: ein Kreis („Feld A → Feld B → Feld A“). Bei einer
 * Pflicht-Folgefrage steht ein Hinweis, dass sie genau die großen Gruppen aufhält. Die Pflicht selbst
 * gilt nur, solange das Feld sichtbar ist (Server und Loader werten dieselbe Regel aus).
 * Die Vorschau „so sieht es aus, wenn …“ füttert die Live-Vorschau rechts (PreviewPane).
 */
import { Card, Checkbox, Icon, Select } from "../../../../ui/components";
import type { WidgetEditor } from "../../useWidgetEditorForm";
import { MAX_BEDINGUNGEN, bereinigeBedingungen, folgefrageFelder, kreisText } from "../../ablauf-modell";

const TITEL = "font-body-sm text-body-sm font-semibold text-on-surface dark:text-inverse-on-surface";
const HILFE = "font-label-md text-label-md text-on-surface-variant";
const IMMER = "always";
const NUR_WENN = "if";

export function FolgefrageEditor({ editor }: { editor: WidgetEditor }) {
  const { form } = editor;
  const felder = folgefrageFelder(form);
  const bereinigt = bereinigeBedingungen(form.bedingungen, felder);
  const kreis = kreisText(bereinigt, felder);
  const anzahl = Object.keys(bereinigt).length;
  // Ausloeser: alle Felder mit Antworten. Eine Frage kann nicht von sich selbst abhaengen.
  const ausloeser = (ziel: string) => felder.filter((f) => f.key !== ziel && f.antworten !== null && f.antworten.length > 0);

  function modusWechsel(ziel: string, modus: string) {
    if (modus === IMMER) {
      editor.bedingungSetzen(ziel, null);
      return;
    }
    const erster = ausloeser(ziel)[0];
    if (!erster || !erster.antworten) return;
    editor.bedingungSetzen(ziel, { feld: erster.key, werte: [(erster.antworten[0] as { wert: string }).wert] });
  }

  function ausloeserWechsel(ziel: string, feld: string) {
    const quelle = felder.find((f) => f.key === feld);
    if (!quelle?.antworten?.[0]) return;
    editor.bedingungSetzen(ziel, { feld, werte: [quelle.antworten[0].wert] });
  }

  function wertUmschalten(ziel: string, wert: string) {
    const b = form.bedingungen[ziel];
    if (!b) return;
    const werte = b.werte.includes(wert) ? b.werte.filter((w) => w !== wert) : [...b.werte, wert];
    // Mindestens eine Antwort muss bleiben, sonst waere die Bedingung leer.
    if (werte.length === 0) return;
    editor.bedingungSetzen(ziel, { feld: b.feld, werte });
  }

  function vorschau(ziel: string) {
    const b = bereinigt[ziel];
    if (!b) return;
    editor.setVorschauWerte({ [b.feld]: b.werte[0] as string });
  }

  return (
    <Card className="space-y-3 p-4" data-testid="folgefragen-editor">
      <div>
        <p className={TITEL}>
          Folgefragen ({anzahl}/{MAX_BEDINGUNGEN})
        </p>
        <p className={`mt-1 ${HILFE}`}>
          Ein Feld erscheint erst, wenn eine Antwort davor es braucht. So bleibt das Formular kurz. Empfohlen: Folgefrage statt Pflichtfeld für
          alle, denn wer nicht betroffen ist, sieht die Frage gar nicht.
        </p>
      </div>

      {kreis && (
        <p role="alert" className="rounded-lg bg-error/10 p-2 font-label-md text-label-md text-error" data-testid="folgefragen-kreis">
          <b>Die Folgefragen drehen sich im Kreis:</b> {kreis}. Bitte eine dieser Bedingungen entfernen. Gespeichert wird erst, wenn das stimmt.
        </p>
      )}

      {felder.length === 0 ? (
        <p className={HILFE}>Es gibt noch keine Felder, an die sich eine Folgefrage knüpfen ließe.</p>
      ) : (
        <ul className="space-y-2">
          {felder.map((f) => {
            const b = form.bedingungen[f.key];
            const aktiv = Boolean(b && bereinigt[f.key]);
            const quellen = ausloeser(f.key);
            const quelle = b ? felder.find((x) => x.key === b.feld) : undefined;
            const kannBedingung = quellen.length > 0;
            const nochFrei = anzahl < MAX_BEDINGUNGEN;
            return (
              <li key={f.key} className="rounded-xl bg-surface-container-lowest p-3 dark:bg-white/[0.03]" data-testid={`folgefrage-${f.key}`}>
                <div className="flex flex-wrap items-center gap-2">
                  <span className="font-body-sm text-body-sm font-semibold text-on-surface dark:text-inverse-on-surface">{f.label}</span>
                  {f.ort && <span className="rounded-full bg-surface-container-high px-2 py-0.5 font-label-md text-[11px] text-on-surface-variant">{f.ort}</span>}
                  {!aktiv && <span className="font-label-md text-label-md text-on-surface-variant">Immer zeigen</span>}
                </div>
                <div className="mt-2 flex flex-wrap items-end gap-2">
                  <label className="w-[190px] max-w-full">
                    <span className={`mb-1 block ${HILFE}`}>Anzeige</span>
                    <Select
                      ariaLabel={`Anzeige von ${f.label}`}
                      value={aktiv ? NUR_WENN : IMMER}
                      disabled={!aktiv && (!kannBedingung || !nochFrei)}
                      onValueChange={(v) => modusWechsel(f.key, v)}
                      options={[
                        { value: IMMER, label: "Immer zeigen" },
                        { value: NUR_WENN, label: "Nur zeigen, wenn …" },
                      ]}
                    />
                  </label>
                  {aktiv && b && (
                    <label className="w-[220px] max-w-full">
                      <span className={`mb-1 block ${HILFE}`}>Wenn dieses Feld …</span>
                      <Select
                        ariaLabel={`Auslöser für ${f.label}`}
                        value={b.feld}
                        onValueChange={(v) => ausloeserWechsel(f.key, v)}
                        options={quellen.map((q) => ({ value: q.key, label: q.label }))}
                      />
                    </label>
                  )}
                </div>
                {aktiv && b && quelle?.antworten && (
                  <fieldset className="mt-2">
                    <legend className={`mb-1 ${HILFE}`}>… diese Antwort hat</legend>
                    <div className="flex flex-wrap gap-x-4 gap-y-1">
                      {quelle.antworten.map((w) => (
                        <label key={w.wert} className="flex items-center gap-2">
                          <Checkbox checked={b.werte.includes(w.wert)} onChange={() => wertUmschalten(f.key, w.wert)} />
                          <span className="font-body-sm text-body-sm text-on-surface dark:text-inverse-on-surface">{w.label}</span>
                        </label>
                      ))}
                    </div>
                  </fieldset>
                )}
                {!aktiv && !kannBedingung && (
                  <p className={`mt-1 ${HILFE}`}>Es gibt noch kein Auswahl- oder Ankreuzfeld, das eine Folgefrage auslösen könnte.</p>
                )}
                {aktiv && f.pflicht && (
                  <p role="status" className="mt-2 rounded-lg bg-tertiary-container/60 p-2 font-label-md text-label-md text-on-tertiary-container" data-testid={`folgefrage-pflicht-${f.key}`}>
                    <b>Das ist erlaubt.</b> Achtung: Eine Pflicht-Folgefrage hält genau die großen Gruppen auf, die am meisten wert sind. Empfohlen:
                    optional. Die Pflicht gilt nur, solange das Feld sichtbar ist.
                  </p>
                )}
                {aktiv && (
                  <div className="mt-2 flex flex-wrap items-center gap-2">
                    <button
                      type="button"
                      className="inline-flex items-center gap-1 font-label-md text-label-md text-primary hover:underline"
                      onClick={() => vorschau(f.key)}
                    >
                      <Icon name="visibility" className="text-[16px]" /> So sieht es aus, wenn …
                    </button>
                    <a href="#widget-editor-preview" className={`${HILFE} hover:underline lg:hidden`}>
                      Zur Vorschau springen
                    </a>
                  </div>
                )}
              </li>
            );
          })}
        </ul>
      )}
      <p className={HILFE}>Kreise sind nicht möglich: Ein Feld kann nicht von sich selbst oder von einem Feld abhängen, das wiederum von ihm abhängt.</p>
    </Card>
  );
}
